import BaseModelComponent from '../../base/base_model_component';
import _ from '../../utils/underscore';
import {CONTAINER_DIRECTION} from './scrollable_container_consts';


class ScrollableContainerModel extends BaseModelComponent {
    /**
     * @constructor
     * @param params
     */
    init(params = {}) {
        super.init(params);
        this._initParams(params)
            ._setComponentID(params.ID);
    }

    _defaultArrowsPadding = [0, 0, 0, 0];


    /**
     * Init parameters
     * @param params
     * @returns {ScrollableContainerModel}
     * @private
     */
    _initParams(params) {
        this._setItems(params.items)
            ._setDirection(params.direction)
            ._setArrowsPadding(params.arrowsPadding)
            ._setItemGap(params.itemGap)
            ._setIsArrowVisible(params.isArrowVisible)
            ._setScrollingStep(params.scrollingStep)
            ._setScrollingDuration(params.scrollingDuration)
            ._setGrid(params.grid)
            ._setIsCenterItems(params.isCenterItems)
            ._setIsSwiper(params.swipeScroll)
            ._setIsDraggable(params.isDraggable)
            ._setIsHasDots(params.isHasDots)
            ._setIsExternalScroll(params.externalScroll)
            ._setSwiperOffset(params.offset);
        return this;
    }

    /**
     * Save new style in model
     * @param params
     */
    setStyle(params) {
      this._initParams(params);
    }

    /**
     * Set HTML Elements for container
     * @param items
     * @private
     */
    _setItems(items = []) {
        this._items = !_.isArray(items) || !this._isHTMLElementsArray(items) ? [] : items;
        return this;
    }


    /**
     * Set items direction (horizontal or vertical)
     * @param direction
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setDirection(direction = CONTAINER_DIRECTION.HORIZONTAL) {
        this._direction = !_.isString(direction) ? CONTAINER_DIRECTION.HORIZONTAL : direction;
        return this;
    }


    /**
     * Set padding area for arrows.
     * Arrows will be places in the center of this area if exist
     * Be carefull, if you use arrows without definition of paddingArea, arrows render inside the container
     * @param arrowsPadding
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setArrowsPadding(arrowsPadding) {
        this._arrowsPadding = this._defaultArrowsPadding
        if (!_.isArray(arrowsPadding) || (arrowsPadding.length !== 2 && arrowsPadding.length !== 4)) {
            this.logger.warn('arrowsPadding parameter is incorrect or not defined. Arrows might be rendered inside the container.');
            return this;
        }
        if (!this._isIntegerArray(arrowsPadding)) {
            this.logger.warn('arrowsPadding parameter contains non integer values. Arrows might be rendered inside the container.');
            return this;
        }
        if (arrowsPadding.length === 2) {
            this._arrowsPadding = [arrowsPadding[0], arrowsPadding[1], arrowsPadding[0], arrowsPadding[1]];
            return this;
        }
        this._arrowsPadding = arrowsPadding;
        return this;
    }


    /**
     * Set gap in pixels between items. First and last items don't get this gap
     * @param itemGap
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setItemGap(itemGap) {
        this._itemGap = !isNaN(itemGap) ? itemGap : 0;
        return this;
    }


    /**
     * Set if component should render the arrows
     * @param isArrowVisible
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setIsArrowVisible(isArrowVisible) {
        this._isArrowVisible = _.isBoolean(isArrowVisible) ? isArrowVisible : true;
        return this;
    }


    /**
     * Set if items should be centered if scroll is not exist
     * @param isCenterItems
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setIsCenterItems(isCenterItems) {
        this._isCenterItems = _.isBoolean(isCenterItems) ? isCenterItems : true;
        return this;
    }


    /**
     * Set if container should be scrolled by swipe (mobile implementation).
     * Supported only in horizontal mode
     * @param touchScroll
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setIsSwiper(swipeScroll = false) {
        this._isSwipeScroll = swipeScroll;
        return this;
    }


    _setIsDraggable(isDraggable = false) {
        this._isDraggable = isDraggable;
        return this;
    }


    _setIsHasDots(isHasDots = false) {
        this._isHasDots = isHasDots;
        return this;
    }


    /**
     * Set if the container has external scrolling. In this case we won't add overflow:auto rule
     * @param externalScroll
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setIsExternalScroll(externalScroll = false) {
        this._externalScroll = externalScroll;
        return this;
    }


    /**
     * Set offset for the first swiper item
     * @param offset
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setSwiperOffset(offset = 'center') {
        this._swiperOffset = offset;
        return this;
    }


    /**
     * Set how items should be scrolled by one arrow click. Minimum is one
     * @param scrollingStep
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setScrollingStep(scrollingStep) {
        this._scrollingStep = isNaN(scrollingStep) || scrollingStep < 1 ? 1 : scrollingStep;
        return this;
    }


    /**
     * Set speed of scrolling in milliseconds, Default is 200
     * @param scrolllingSpeed
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setScrollingDuration(scrollingDuration) {
        this._scrollingDuration = isNaN(scrollingDuration) || scrollingDuration < 0 ? 200 : scrollingDuration;
        return this;
    }


    /**
     * Set items grid
     * @param grid
     * itemsInRow - how much items will be in each line (row in Vertical and column in horizontal)
     * gap - gap between items in one row (margin-right in vertical and margin-bottom in horizontal)
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setGrid(grid) {
        let defaultGrid = {itemsInRow: 1, gap: 0};
        this._grid = !grid || !_.isObject(grid) ? defaultGrid : Object.assign(defaultGrid, grid);
        return this;
    }


    /**
     * Set if of component. If id is undefined or null, random id will be generated
     * @param name
     * @returns {ScrollableContainerModel}
     * @private
     */
    _setComponentID(id) {
        this._componentID = !id || !_.isString(id) ? _.getGuid() : id;
        return this;
    }


    /**
     * Returns if the array contains only HTML Elements
     * @param array
     * @returns {boolean}
     * @private
     */
    _isHTMLElementsArray(array) {
        for (let i = 0; i < array.length; i++) {
            if (!_.isElement(array[i])) {
                return false;
            }
        }
        return true;
    }

    /**
     * Check if the array is an array of integers
     * @param array
     * @returns {boolean}
     * @private
     */
    _isIntegerArray(array) {
        for (let i = 0; i < array.length; i++) {
            if (isNaN(array[i])) {
                return false;
            }
        }
        return true;
    }

    /**
     * Get HTML Elements for container
     * @returns {*}
     */
    getItems() {
        return this._items;
    }


    /**
     * Get items direction
     * @returns {string|*}
     */
    getDirection() {
        return this._direction;
    }


    /**
     * Get padding area definiton to center arrows
     * @returns {Array|[number,number,number,number]}
     */
    getArrowsPadding() {
        return this._arrowsPadding;
    }


    /**
     * Get gap between items
     * @returns {number|*}
     */
    getItemGap() {
        return this._itemGap;
    }


    /**
     * Get if arrows should be visible
     * @returns {boolean|*}
     */
    getIsArrowVisible() {
        return this._isArrowVisible;
    }


    /**
     * Get if items should be centered if scroll doesn't exist
     * @returns {boolean|*}
     */
    getIsCenterItems() {
        return this._isCenterItems;
    }


    /**
     * Get how items will be scrolled by one arrow click
     * @returns {number|*}
     */
    getScrollingStep() {
        return this._scrollingStep;
    }


    /**
     * Get scrolling duration in milliseconds
     * @returns {*}
     */
    getScrollingDuration() {
        return this._scrollingDuration;
    }


    /**
     * Get grid settings
     * @returns {{itemsInRow: number, gap: number}|*}
     */
    getGrid() {
        return this._grid;
    }


    /**
     * Get if container should be scrolled by touch / drag
     * @returns {boolean|*}
     */
    getIsSwiper() {
        return this._isSwipeScroll;
    }


    getIsDraggable() {
        return this._isDraggable;
    }


    /**
     *
     * @returns {boolean|*}
     */
    getIsExternalScroll() {
        return this._externalScroll;
    }


    getIsHasDots() {
        return this._isHasDots;
    }


    getSwiperOffset() {
        return this._swiperOffset;
    }


    /**
     * Get id of component
     * @returns {string|*}
     */
    getComponentID() {
        return this._componentID;
    }
}
export default ScrollableContainerModel;